import { Component, DoCheck, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import type { Field } from '../../model/formBuilderInterface';
import { FormsModule } from '@angular/forms';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { CommonModule } from '@angular/common';
import { TimesCircleIcon } from 'primeng/icons/timescircle';
import { CheckboxModule } from 'primeng/checkbox';
import { ValidationErrorsComponent } from '../validation-errors/validation-errors.component';
import { Subject, takeUntil } from 'rxjs';
import { ToolTipDirective } from '@shared/directive/tool-tip.directive';

@Component({
  selector: 'app-custom-multiselect',
  templateUrl: './custom-multiselect.component.html',
  styleUrls: ['./custom-multiselect.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiSelectModule,
    CheckboxModule,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    ToolTipDirective,
    TimesCircleIcon
  ]
})
export class CustomMultiSelectComponent implements OnInit, DoCheck, OnDestroy {
  @Input() field: Field;
  @Input() form: FormGroup;
  searchTerm: string = '';
  allSelected = false;
  currentOptions;
  allOptions = []; // full source
  filteredOptions = [];
  private lastOptionsRef: unknown[] | null = null;
  private lastSelectedValueRef: unknown[] | null = null;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.refreshOptions();
    this.subscribeField();
  }

  ngDoCheck(): void {
    const options = this.field?.options ?? null;
    const selectedValue = this.form?.get(this.field?.name)?.value ?? null;
    if (
      options !== this.lastOptionsRef ||
      selectedValue !== this.lastSelectedValueRef
    ) {
      this.refreshOptions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshOptions(): void {
    this.lastOptionsRef = this.field?.options ?? null;
    this.lastSelectedValueRef = this.form?.get(this.field?.name)?.value ?? null;
    this.allOptions = this.field?.options || [];
    this.filterOptions();
  }

  subscribeField() {
    this.form
      .get(this.field.name)
      .valueChanges?.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        if (val) {          
          if (val.length === this.field?.options?.length) {
            this.allSelected = true;
          } else {
            this.allSelected = false;
          }
        }

        this.allOptions = this.field?.options || this.allOptions;
        this.lastSelectedValueRef = val ?? null;
        this.filterOptions();
      });
  }

  private getOptionUniqueId(opt: Record<string, unknown> | null | undefined): string | null {
    const config = this.field?.customMultiselectConfig;
    const dataKey = config?.dataKey;
    if (dataKey && opt?.[dataKey] != null && opt[dataKey] !== '') {
      return String(opt[dataKey]);
    }
    if (opt?.sk) {
      return String(opt.sk);
    }
    if (opt?.pk) {
      return String(opt.pk);
    }
    const labelKey = config?.optionLabel;
    if (labelKey && opt?.[labelKey] != null && opt[labelKey] !== '') {
      return String(opt[labelKey]);
    }
    return null;
  }

  filterOptions() {
    const term = this.searchTerm?.toLowerCase().trim() || '';
    const filtered = term
      ? this.allOptions.filter((opt) =>
          opt[this.field.customMultiselectConfig.optionLabel]
            ?.toLowerCase()
            .includes(term)
        )
      : [...this.allOptions];

    const selectedOptions: Record<string, unknown>[] =
      this.form.get(this.field.name)?.value || [];

    const seenIds = new Set<string>();
    this.filteredOptions = [...selectedOptions, ...filtered].filter((opt) => {
      const uniqueId = this.getOptionUniqueId(opt);
      if (!uniqueId || seenIds.has(uniqueId)) {
        return false;
      }
      seenIds.add(uniqueId);
      return true;
    });
  }

  isSelectedItemRemovable(item: Record<string, unknown>): boolean {
    const nonRemovableKey =
      this.field?.customMultiselectConfig?.nonRemovableOptionKey;
    return !nonRemovableKey || !item?.[nonRemovableKey];
  }

  removeSelectedItem(
    multiSelect: MultiSelect,
    item: Record<string, unknown>,
    event: MouseEvent
  ): void {
    event.stopPropagation();
    multiSelect.removeChip(item, event);
  }

  toggleAll(event: boolean) {
    this.allSelected = event;
    if (this.allSelected) {
      this.form.get(this.field.name).setValue(this.field.options);
    } else {
      this.form.get(this.field.name).setValue([]);
    }
  }

  confirm(multiSelect: any) {
    this.closeMultiSelect(multiSelect);
    if (this.field.callback) this.field.callback('confirm');
  }

  customBtnClick(multiSelect: any) {
    if (this.field?.customMultiselectConfig?.customButtonDisabled) {
      return;
    }
    this.closeMultiSelect(multiSelect);
    if (this.field.callback) this.field.callback('custom');
  }

  closeMultiSelect(multiSelect: any) {
    multiSelect.hide();
  }

  get customButtonTooltip(): string {
    const config = this.field?.customMultiselectConfig;
    if (!config?.customButtonDisabled || !config?.customButtonDisabledMessage) {
      return '';
    }
    return config.customButtonDisabledMessage;
  }
}
